import {
  GetSubscriptionRevenueQuery,
  SubscriptionRevenue,
  useGetSubscriptionRevenueQuery,
  useSetRevenueSplitRulesMutation,
} from '../../../../../../generated/graphql';
import { Actions, Async, F1, TypedOmit } from '@client/new/helpers/types';
import { useAsyncResult } from '@client/new/helpers/gql';
import toast from 'react-hot-toast';

type useRevenueModelProps = {
  influencerId: number;
};
export type RevenueModel = TypedOmit<SubscriptionRevenue, '__typename'>;

export type RevenueViewActionsMap = {
  cancelRevenueSplitEdit: never;
  saveRevenueSplit: never;
  editRevenueSplit: never;
  updateSplitRule: { value: number | undefined; productType: string };
};

export type RevenueViewActions = Actions<RevenueViewActionsMap>;
export type RevenueViewDispatcher = F1<RevenueViewActions>;

type RevenueModelAction = Actions<{
  saveRevenueSplit: { referral: number; playbook: number };
}>;

export type RevenueModelDispatcher = F1<RevenueModelAction>;

export const useRevenueModel = ({
  influencerId,
}: useRevenueModelProps): Async<
  RevenueModel & { dispatch: RevenueModelDispatcher }
> => {
  const subscriptionRevenueQuery = useGetSubscriptionRevenueQuery({
    variables: {
      influencerId,
    },
  });

  const [setRevenueSplitRulesMutation] = useSetRevenueSplitRulesMutation();

  const saveRevenueSplit = async ({
    referral,
    playbook,
  }: {
    referral: number;
    playbook: number;
  }) => {
    return setRevenueSplitRulesMutation({
      variables: {
        data: {
          influencerId,
          referral,
          playbook,
        },
      },
    })
      .then(() => {
        toast.success('Rule was updated successfully');
      })
      .catch(() => {
        toast.error('Something went wrong');
        subscriptionRevenueQuery.refetch();
      });
  };

  const dispatch: RevenueModelDispatcher = (action) => {
    switch (action.type) {
      case 'saveRevenueSplit':
        return saveRevenueSplit(action.payload);
    }
  };

  const asyncSubscriptionRevenue = useAsyncResult<GetSubscriptionRevenueQuery>(
    subscriptionRevenueQuery,
  );
  if (asyncSubscriptionRevenue.type !== 'fetched')
    return asyncSubscriptionRevenue;

  if (!asyncSubscriptionRevenue.value.subscriptionRevenue)
    return { type: 'fetch-error', value: 'Could not fetch data' };

  return {
    type: 'fetched',
    value: {
      ...asyncSubscriptionRevenue.value.subscriptionRevenue,
      dispatch,
    },
  };
};
